﻿import React from "react";
import ReactDOM from 'react-dom';
import { LiveDataSet, Status } from "../../API/LiveData/LiveDataSet";
import { ParameterSchemaStation } from "../../API/Schema/ParameterSchema";
import { ILiveDataListenerContext, ILiveDataListener } from "../../ILiveDataListener";
import { MainSite } from "../../MainSite";
import { WarningGroup } from "../../Model/WarningGroup";
import { ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { ISiteObservationsPage } from "./ISiteObservationsPage";

export class Penguins_Lifeboat implements ISiteObservationsPage {

    public id: string = 'Penguins_Lifeboat';
    public label: string = 'Lifeboats';

    public station: ParameterSchemaStation;
    public warningGroup: WarningGroup;

    constructor(public mainSite: MainSite, public ctx: ILiveDataListenerContext) {
    }

    public notifyResize() {
    }

    public show(station: ParameterSchemaStation): boolean {
        return station.Station.Id === "NorthSea_Penguins";
    }

    public render(container: JQuery, listeners: ILiveDataListener[], station: ParameterSchemaStation, warningGroup: WarningGroup): void {
        this.station = station;
        this.warningGroup = warningGroup;

        const page = $('<div class="midi_SiteObservations_padded_page_content midi_siteObsPage_penguinsLifeboats"></div>').appendTo(container);
        ReactDOM.render(<PageComponent latestData={this.mainSite.liveDataManager.latestData} listeners={listeners} />, page[0]);
    }
}

const getLatestDataValue = (latestData: LiveDataSet, groupId: string): string => {
    const values = latestData?.Stations["NorthSea_Penguins"]?.GroupValues[groupId]?.filter(x => x.Primary && (x.ExpiryStatus === Status.Good || x.ExpiryStatus === Status.Expiring)) ?? [];
    return values.length ? values[0].Value : "";
}

const getLatestDataValueNum = (latestData: LiveDataSet, groupId: string): number => {
    const values = latestData?.Stations["NorthSea_Penguins"]?.GroupValues[groupId]?.filter(x => x.Primary && (x.ExpiryStatus === Status.Good || x.ExpiryStatus === Status.Expiring)) ?? [];
    return values.length ? parseFloat(values[0].Value) : NaN;
}

const PageComponent: React.FC<{ latestData: LiveDataSet; listeners: ILiveDataListener[]; }> = (props) => {
    const [pitch, setPitch] = React.useState(NaN);
    const [roll, setRoll] = React.useState(NaN);
    const [incl, setIncl] = React.useState(NaN);

    const [hullStateAuto, setHullStateAuto] = React.useState(true);
    const [hullState, setHullState] = React.useState<"" | "intact" | "damaged">("");
    const hullStateError = "";

    const [highLifeboatAuto, setHighLifeboarAuto] = React.useState(true);
    const [highLifeboat, setHighLifeboat] = React.useState<"" | "l1" | "l2">("");
    const highLifeboatError = hullState === "damaged" && highLifeboat === "" ? "Select high lifeboat" : "";

    const [hsStr, setHsStr] = React.useState("");
    const [hsAuto, setHsAuto] = React.useState(true);
    const hsMatch = /^\d+(\.\d*)?$/.test(hsStr);
    const hs = parseFloat(hsStr);
    const hsError = !hsMatch ? "Please enter a number"
        : hs < 0 || hs > 30 ? "Value outside valid range 0-30"
        : "";

    const [waveDirStr, setWaveDirStr] = React.useState("");
    const [waveDirAuto, setWaveDirAuto] = React.useState(true);
    const waveDirMatch = /^\d+(\.\d*)?$/.test(waveDirStr);
    const waveDir = parseFloat(waveDirStr);
    const waveDirError = !waveDirMatch ? "Please enter a number"
        : waveDir < 0 || waveDir > 360 ? "Value outside valid range 0-360"
        : "";

    const [latestData, setLatestData] = React.useState(props.latestData);

    const liveDataChanged = (latestData: LiveDataSet) => {
        setLatestData(latestData);
    };

    const listenerPushed = React.useRef(false);
    React.useEffect(() => {
        if (!listenerPushed.current) {
            listenerPushed.current = true;
            props.listeners.push({
                liveDataChanged
            });
        }
    }, []);
    React.useEffect(() => {
        // WISE group confirmed be email on 06/06/2024 that:
        // Positive roll = roll to starboard
        // Negative pitch = aft pointing down

        const rollLeft = getLatestDataValueNum(latestData, ParameterGroupInfoSet.Roll_Left_deg_Max.Id);
        const rollRight = getLatestDataValueNum(latestData, ParameterGroupInfoSet.Roll_Right_deg_Max.Id);

        const pitchUp = getLatestDataValueNum(latestData, ParameterGroupInfoSet.PitchBowDown_deg_Max.Id);
        const pitchDown = getLatestDataValueNum(latestData, ParameterGroupInfoSet.PitchBowUp_deg_Max.Id);

        let highLifeboat: "" | "l1" | "l2" = "";
        if (!isNaN(rollLeft) && !isNaN(rollRight)) {
            const avgRoll = 0.5 * (rollLeft + rollRight);
            const rollToStarboard = avgRoll > 0;
            highLifeboat = rollToStarboard ? "l1" : "l2";
        }

        let hullState: "" | "intact" | "damaged" = "";
        if (!isNaN(rollLeft) && !isNaN(rollRight) && !isNaN(pitchUp) && !isNaN(pitchDown)) {
            const avgRoll = 0.5 * (rollLeft + rollRight);
            const avgPitch = 0.5 * (pitchUp + pitchDown);
            const incl = (180 / Math.PI) * Math.acos(
                Math.cos((Math.PI / 180) * avgRoll) *
                Math.cos((Math.PI / 180) * avgPitch)
            );
            setPitch(avgPitch);
            setRoll(avgRoll);
            setIncl(incl);

            hullState = incl > 4 ? "damaged" : "intact";
        }

        if (hullStateAuto) setHullState(hullState);
        if (highLifeboatAuto) setHighLifeboat(highLifeboat);
        if (hsAuto) setHsStr(getLatestDataValue(latestData, ParameterGroupInfoSet.Hs_m.Id));
        if (waveDirAuto) setWaveDirStr(getLatestDataValue(latestData, ParameterGroupInfoSet.PeakWaveDirection_deg.Id));

        if (hullStateError || highLifeboatError || hsError || waveDirError) {
            note = undefined;
        }
    });

    let note: undefined | React.ReactElement = undefined;

    if (hullStateError || highLifeboatError || hsError || waveDirError) {

        note = undefined;

    } else {

        if ((hullState === "intact") || (hullState === "damaged" && highLifeboat === "")) {

            if ((hs > 7) && (waveDir >= 37.5 && waveDir <= 262.5)) {
                if (waveDir >= 37.5 && waveDir <= 150) {
                    note =
                        <div>
                            Use Lifeboat 1 as preference
                        </div>
                } else if (waveDir > 150 && waveDir <= 262.5) {
                    note =
                        <div>
                            Use Lifeboat 2 as preference
                        </div>
                }
            } else {
                note =
                    <div>
                            Use either/both lifeboats
                    </div>
            }
        } else if ((hullState === "damaged") && (highLifeboat === "l1" || highLifeboat === "l2")) {
            if (highLifeboat === "l1") {
                if (hs > 3.5) {
                    if (hs > 8.5) {
                        if (waveDir >= 116 && waveDir <= 139 && hs > 16.5) {
                            note =
                                <div>
                                        Use Lifeboat 1 as preference
                                </div>
                        } else {
                            note =
                                <div>
                                    Use Lifeboat 2 as preference
                                </div>
                        }
                    } else {
                        if ((waveDir >= 94 && waveDir <= 161) || (waveDir >= 251 && waveDir <= 274)) {
                            note =
                                <div>
                                    Use Lifeboat 1 as preference
                                </div>
                        } else {
                            note =
                                <div>
                                    Use Lifeboat 2 as preference
                                </div>
                        }
                    }
                } else {
                    note =
                        <div>
                            Use Lifeboat 2 as preference
                        </div>
                }
            } else if (highLifeboat === "l2") {
                if (hs > 3.5) {
                    if (hs > 8.5) {
                        if (waveDir >= 206 && waveDir <= 229 && hs > 16.5) {
                            note =
                                <div>
                                    Use Lifeboat 2 as preference
                                </div>
                        } else {
                            note =
                                <div>
                                        Use Lifeboat 1 as preference
                                </div>
                        }
                    } else {
                        if ((waveDir >= 26 && waveDir <= 49) || (waveDir >= 184 && waveDir <= 251)) {
                            note =
                                <div>
                                    Use Lifeboat 2 as preference
                                </div>
                        } else {
                            note =
                                <div>
                                    Use Lifeboat 1 as preference
                                </div>
                        }
                    }
                } else {
                    note =
                        <div>
                            Use Lifeboat 1 as preference
                        </div>
                }
            }
        }
    }

    return (
        <>
            <React.StrictMode>
                <table className="inputTable">
                    <tbody>
                        <tr>
                            <th>Hull State</th>
                            <td>
                                <input type="checkbox" checked={hullStateAuto} onChange={e => setHullStateAuto(e.target.checked)} /> Auto
                            </td>
                            <td>
                                <select value={hullState} disabled={hullStateAuto} onChange={e => setHullState(e.target.value as any)}>
                                    <option value="intact">Intact - Inclination &lt;= 4&deg;</option>
                                    <option value="damaged">Damaged - Inglination &gt; 4&deg;</option>
                                </select>
                                <br />
                                <>WMS reports Roll = {roll.toFixed(1)}&deg;, Pitch = {pitch.toFixed(1)}&deg;, Incl = {incl.toFixed(1)}&deg;</>
                            </td>
                            <td className="warning">{hullStateError}</td>
                        </tr>
                        {hullState === "damaged" && <tr>
                            <th>Which lifeboat is high?</th>
                            <td>
                                <input type="checkbox" checked={highLifeboatAuto} onChange={e => setHighLifeboarAuto(e.target.checked)} /> Auto
                            </td>
                            <td>
                                <select value={highLifeboat} disabled={highLifeboatAuto} onChange={e => setHighLifeboat(e.target.value as any)}>
                                    <option hidden value="">Select</option>
                                    <option value="l1">Lifeboat 1 WEST is high</option>
                                    <option value="l2">Lifeboat 2 EAST is high</option>
                                </select>
                            </td>
                            <td className="warning">{highLifeboatError}</td>
                        </tr>}
                        <tr>
                            <th>Significant Wave Height (m)</th>
                            <td>
                                <input type="checkbox" checked={hsAuto} onChange={e => setHsAuto(e.target.checked)} /> Auto
                            </td>
                            <td>
                                <input type="text" disabled={hsAuto} value={hsStr} onChange={e => setHsStr(e.target.value)} />
                            </td>
                            <td className="warning">{hsError}</td>
                        </tr>
                        <tr>
                            <th>Wave Direction (&deg; from True North)</th>
                            <td>
                                <input type="checkbox" checked={waveDirAuto} onChange={e => setWaveDirAuto(e.target.checked)} /> Auto
                            </td>
                            <td>
                                <input type="text" disabled={waveDirAuto} value={waveDirStr} onChange={e => setWaveDirStr(e.target.value)} />
                            </td>
                            <td className="warning">{waveDirError}</td>
                        </tr>
                    </tbody>
                </table>

                {note !== undefined && <div className="lifeboatStatus" style={{
                    backgroundColor: "#e2efd9",
                    textAlign: "center"
                }}>
                    {note}
                </div>}
            </React.StrictMode>
        </>
    );
}